import { action, computed, makeObservable, observable, override, reaction, runInAction } from 'mobx';
import { AssureBaseStore, IAssureStoreConstructorOptions } from './assure-base.store';
import { DeviceExecutionDto, ICreateWelcomePackOptions, WelcomePackDto, WelcomePackStatus } from 'dto/welcome-pack.dto';
import { AccountConfigurationApiClient } from 'api/account-configuration-api-client';
import { isNullOrEmpty, sleep } from 'common-utils';
import { WelcomePackUtils } from 'pages/private/devices/welcome-pack/utils';

export class WelcomePackStore extends AssureBaseStore<AccountConfigurationApiClient, WelcomePackDto> {
    protected get apiClient(): AccountConfigurationApiClient {
        return this.apiClientStore.apiClients.accountConfiguration;
    }

    constructor(options: IAssureStoreConstructorOptions) {
        super(options);
        makeObservable(this);
        reaction(
            () => ({ displayAccountUuid: this._displayedAccountUuid, welcomePacks: this._accountUuidToWelcomePacks.get(this._displayedAccountUuid) }),
            () => {
                if (isNullOrEmpty(this._displayedAccountUuid)) {
                    this.setEntities([]);
                    return;
                }
                const welcomePacks = this._accountUuidToWelcomePacks.get(this._displayedAccountUuid) || [];
                this.setEntities(welcomePacks);
            },
            {
                equals: (a, b) => {
                    return a.displayAccountUuid === b.displayAccountUuid && a.welcomePacks === b.welcomePacks;
                }
            },
        )
    }

    @observable
    private _accountUuidToWelcomePacks: Map<string, WelcomePackDto[]> = new Map();

    @observable
    private _displayedAccountUuid: string | null = null;

    @action
    setDisplayedAccountUuid(accountUuid: string | null) {
        this._displayedAccountUuid = accountUuid;
    }

    private _setWelcomePacksForAccount(accountUuid: string, welcomePacks: WelcomePackDto[]) {
        this._accountUuidToWelcomePacks.set(accountUuid, welcomePacks.sort((a, b) => a.label?.localeCompare(b.label)));
    }

    private _addWelcomePackToAccount(accountUuid: string, welcomePack: WelcomePackDto) {
        const existing = this._accountUuidToWelcomePacks.get(accountUuid) || [];
        const updated = [...existing, welcomePack];
        this._setWelcomePacksForAccount(accountUuid, updated);
    }

    async createWelcomePack(apiAccountId: number, accountUuid: string, wpConfiguration: ICreateWelcomePackOptions): Promise<WelcomePackDto> {
        const result = await this.apiClient.createWelcomePack(apiAccountId, accountUuid, wpConfiguration);
        const newWelcomePack = new WelcomePackDto(result.welcomePack);
        runInAction(() => {
            this._addWelcomePackToAccount(accountUuid, newWelcomePack);
        });
        return newWelcomePack;
    }

    async getWelcomePacks(apiAccountId: number, accountUuid: string, force?: boolean): Promise<WelcomePackDto[]> {
        try {
            this.setDataLoading(true);
            this.setDisplayedAccountUuid(accountUuid);
            if (!force && this._accountUuidToWelcomePacks.has(accountUuid)) {
                const cached = this._accountUuidToWelcomePacks.get(accountUuid)!;
                return cached;
            }

            const result = await this.apiClient.getWelcomePacks(apiAccountId, accountUuid);
            const welcomePacks = result.welcomePacks.map((wp) => new WelcomePackDto(wp));
            runInAction(() => {
                this._setWelcomePacksForAccount(accountUuid, welcomePacks);
            });
            return welcomePacks;
        } catch (err) {
            throw err;
        } finally {
            this.setDataLoading(false);
        }
    }

    async inactivateWelcomePack(apiAccountId: number, accountUuid: string, welcomePackId: number, force?: boolean): Promise<{ updated: boolean }> {
        const response = await this.apiClient.inactivateWelcomePack(apiAccountId, accountUuid, welcomePackId, force);
        if (response.updated) {
            runInAction(() => {
                for (const [accountUuid, packs] of this._accountUuidToWelcomePacks.entries()) {
                    const index = packs.findIndex(p => p.id === welcomePackId);
                    if (index !== -1) {
                        const updatedPack = new WelcomePackDto({ ...packs[index].toJSON(), status: WelcomePackStatus.INACTIVE });
                        const updatedList = [...packs.slice(0, index), updatedPack, ...packs.slice(index + 1)];
                        this._setWelcomePacksForAccount(accountUuid, updatedList);
                        break;
                    }
                }
            });
        }
        return response;
    }

    async getDeviceExecutionsByStep(apiAccountId: number, accountUuid: string, welcomePackId: number, stepName: string): Promise<DeviceExecutionDto[]> {
        const response = await this.apiClient.getDeviceExecutionsByStep(apiAccountId, accountUuid, welcomePackId, stepName);
        return response.deviceExecutions.map((de) => new DeviceExecutionDto(de));
    }

    @computed
    get welcomePacks() {
        if (this.entities.length === 0) return [];
        return [...this.entities].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }

    findActiveWelcomePackByAccountUuid(accountUuid: string): WelcomePackDto | null {
        if (isNullOrEmpty(this.welcomePacks)) return null;
        return this.welcomePacks.find((wp) => wp.accountUuid === accountUuid && WelcomePackUtils.isActive(wp));
    }

    findWelcomePackByAccountUuidAndId(accountUuid: string, id: number): WelcomePackDto | null {
        if (isNullOrEmpty(this.welcomePacks)) return null;
        return this.welcomePacks.find((wp) => wp.accountUuid === accountUuid && wp.id === id);
    }

    @override
    public clearEntities(): void {
        this.setDisplayedAccountUuid(null);
    }
}
